import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";

import { useAppDispatch } from "$store";
import { removeFilter } from "$actions/app";
import useCompany from "$hooks/useCompany";
import { CompanyType } from "$types/CompanyType";
import { CompanyServices, UserServices } from "$services";
import { CacheInvalidationAction } from "$utils/cache";
import { appState } from "$reducers";
import { usePatchCompany } from "./usePatchCompany";
import { EntrepriseProfileLogic } from "../types";

const { CLIENT, CONTRIBUTOR, SUPPLIER } = CompanyType;

export function useEntrepriseProfileLogic(): EntrepriseProfileLogic {
  const params = useParams<"id" | "idChildCompany" | "idCompany">();
  const id = params?.idChildCompany || params?.idCompany || params?.id || "";
  const dispatch = useAppDispatch();
  const { isClient, isContributor, isSupplier } = useCompany();
  const [selectedTab, setSelectedTab] = useState<CompanyType>(CLIENT);
  const { filter: companySidebarFilter } = useSelector(appState);
  const navigate = useNavigate();

  const {
    isLoading,
    contributors,
    clients,
    suppliers,
    getCompany,
    carrier,
    logiWarehouse,
    clientsWithCatalogue,
  } = usePatchCompany(id);

  useEffect(() => {
    if (companySidebarFilter && navigate) {
      navigate(`/entreprise/${companySidebarFilter?._id}`);
    }
  }, [companySidebarFilter, navigate]);

  const { data: company } = useQuery({
    queryKey: [CacheInvalidationAction.GET_COMPANY_BY_ID, id],
    queryFn: () => CompanyServices.getCompanyById(id),
    enabled: !!id,
    staleTime: 1000 * 60,
  });

  const token = localStorage.getItem("access_token");
  const { data: users, isLoading: isLoadingUsers } = useQuery({
    queryKey: [CacheInvalidationAction.GET_USERS, id],
    queryFn: () => UserServices.FetchUsers(token, { company: id }),
    gcTime: 5 * 60 * 1000,
    enabled: !!id,
  });

  // Specific treatment for a company which is both Client and Contributor.
  const [selectedCompanyTypes, setSelectedCompanyTypes] = useState<
    CompanyType[] | undefined
  >(company?.companyTypes);

  // Agency files management
  const { control, setValue } = useForm({
    mode: "onChange",
  });

  const [formValues, setFormValues] = useState<any>({
    medias: (company?.medias || []).map((media: any) => media || {}),
  });

  useEffect(() => {
    setFormValues({
      medias: (company?.medias || []).map((media: any) => media || {}),
    });
  }, [company]);

  useEffect(() => {
    setValue("medias", formValues.medias, { shouldValidate: false });
  }, [formValues.medias, setValue, formValues]);

  const resetFilter = () => {
    dispatch(removeFilter());
  };

  // Keep the selectedCompanyTypes in sync with the company
  useEffect(() => {
    if (
      isClient(company?.companyTypes) &&
      (isContributor(company?.companyTypes) ||
        isSupplier(company?.companyTypes))
    ) {
      setSelectedCompanyTypes([CLIENT]);
      setSelectedTab(CLIENT);
    } else {
      // Default
      setSelectedCompanyTypes(company?.companyTypes);
      setSelectedTab(company?.companyTypes?.[0]);
    }
  }, [company?.companyTypes, isClient, isContributor, isSupplier]);

  const userList = (users || []).filter((u: any) => !u.isDisable);

  const redirect = (url: string) => {
    resetFilter();
    navigate(url);
  };

  const isNotClientAnymore =
    company?.wasClientOnce && !isClient(company?.companyTypes);
  const isNotContributorAnymore =
    company?.wasContributorOnce && !isContributor(company?.companyTypes);
  const isNotSupplierAnymore =
    company?.wasSupplierOnce && !isSupplier(company?.companyTypes);

  const cantEditCurrentTab =
    (selectedTab === CLIENT && isNotClientAnymore) ||
    (selectedTab === CONTRIBUTOR && isNotContributorAnymore) ||
    (selectedTab === SUPPLIER && isNotSupplierAnymore);

  const handleTabChange = (val: CompanyType) => {
    setSelectedCompanyTypes([val]);
    setSelectedTab(val);
  };

  return {
    id,
    company,
    isLoading,
    isLoadingUsers,
    userList,
    selectedTab,
    selectedCompanyTypes,
    setSelectedTab,
    setSelectedCompanyTypes,
    handleTabChange,
    contributors,
    clients,
    suppliers,
    carrier,
    logiWarehouse,
    clientsWithCatalogue,
    getCompany,
    control,
    redirect,
    isNotClientAnymore: !!isNotClientAnymore,
    isNotContributorAnymore: !!isNotContributorAnymore,
    isNotSupplierAnymore: !!isNotSupplierAnymore,
    cantEditCurrentTab: !!cantEditCurrentTab,
  };
}
